import type { TypedDocumentNode } from '@apollo/client';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { APOLLO_FETCH_POLICY } from '@/constants/apollo';
import { useMemo } from 'react';
import {
  GET_MEMBER_ASSOCIATION,
  GET_MEMBER_ASSOCIATIONS,
  LOOKUP_MEMBERS_BY_IDS,
} from './queries';
import {
  SAVE_MEMBER_ASSOCIATION,
  DELETE_MEMBER_ASSOCIATION,
} from './mutations';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { setDefaultHeader } from '@/publicSearch/graphql/headers';

export type MemberAssociationItem = {
  settingKey: string;
  societyId: number;
  memberId: number;
  memberIdent: string | null;
};

export function useMemberAssociation(skip = false) {
  const orgId = usePublicSearchContext((ctx) => ctx.society.orgId);
  const requestContext = useMemo(
    () => ({ headers: setDefaultHeader(orgId) }),
    [orgId],
  );
  return useQuery(
    GET_MEMBER_ASSOCIATION as TypedDocumentNode<{
      memberAssociation: number[];
    }>,
    {
      skip: skip || !orgId,
      fetchPolicy: APOLLO_FETCH_POLICY.CACHE_FIRST,
      context: requestContext,
    },
  );
}

export function useMemberAssociations(skip = false) {
  const orgId = usePublicSearchContext((ctx) => ctx.society.orgId);
  const requestContext = useMemo(
    () => ({ headers: setDefaultHeader(orgId) }),
    [orgId],
  );
  return useQuery(
    GET_MEMBER_ASSOCIATIONS as TypedDocumentNode<{
      memberAssociations: MemberAssociationItem[];
    }>,
    {
      skip: skip || !orgId,
      fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
      context: requestContext,
    },
  );
}

export function useSaveMemberAssociation() {
  return useMutation(
    SAVE_MEMBER_ASSOCIATION as TypedDocumentNode<
      { saveMemberAssociation: MemberAssociationItem },
      { societyId: number; memberId: number; memberIdent: string }
    >,
    {
      refetchQueries: [GET_MEMBER_ASSOCIATION, GET_MEMBER_ASSOCIATIONS],
    },
  );
}

export function useDeleteMemberAssociation() {
  return useMutation(
    DELETE_MEMBER_ASSOCIATION as TypedDocumentNode<
      { deleteMemberAssociation: boolean },
      { settingKey: string; societyId: number }
    >,
    {
      refetchQueries: [GET_MEMBER_ASSOCIATION, GET_MEMBER_ASSOCIATIONS],
    },
  );
}

type MemberLookupResult = {
  memberId: number;
  societyId: number;
  memberIdent: string | null;
  memberName: string | null;
};

export function useLookupMembersByIds() {
  return useLazyQuery(
    LOOKUP_MEMBERS_BY_IDS as TypedDocumentNode<
      { lookupMembersByIds: MemberLookupResult[] },
      { members: Array<{ memberId: number; societyId: number }> }
    >,
    { fetchPolicy: APOLLO_FETCH_POLICY.CACHE_FIRST },
  );
}
